import { ethers } from 'ethers';
import React, { useEffect, useRef, useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { decodeEventLog } from 'viem';
import { CPGABI, ERC20ABI } from '../../ABI/ABI';
import Loader from '../../components/loader/Loader';
import Token from '../../components/tokenList/Token';
import { CPGAddress, targetChainId, ZERO_ADDRESS } from '../../constant/constant';
import { CONTRACT_ADDRESS_WARNING_MSG, SELF_PAYMENT_WARNING_MSG, TRANSACTION_ERROR_MSG, TRANSACTION_SUCCESS_MSG, USER_REJECT_TRANSACTION_MSG } from '../../constant/toasterMessage';
import { convertBigToSmallDigitWithSpecificDecimal, convertInTokenBigAmount, fireErrorToast, fireSuccessToast, getAllowance, handleKeyDown, handleWheel, isContractAddress } from '../../utils/commonFunction';
import { ContractWrite } from '../../utils/contractInstance';
import { publicClient } from '../../utils/wagmiConfig';
import { persistPaymentRecord } from '../../api/backendClient';
import {
    decodePaymentCreatedId,
    paymentRefBytes32,
} from '../../utils/cpgDiamondReader';
// import { getSingleHopQuote } from './swapRead'

const DirectPayment = ({ embedded = false, basics = null }) => {
    const [directPaymentData, setDirectPaymentData] = useState({ userName: '', fromAmount: '', to: '' });
    const [fromToken, setFromToken] = useState({});
    const [note, setNote] = useState('')
    const [buttonDetail, setButtonDetail] = useState({ name: "", bgColor: "bg-gray-600", textColor: 'text-gray-600', isDisabled: false });
    const [loading, setLoading] = useState(false)
    const [isRecipientContract, setIsRecipientContract] = useState(false)

    const { address: walletAddress } = useAccount()
    const { signMessageAsync } = useSignMessage()

    useEffect(() => {
        if (!embedded || !basics) return;
        setDirectPaymentData((prev) => ({
            ...prev,
            userName: basics.userName || '',
            fromAmount: basics.amount || '',
        }));
        if (basics.token?.address) {
            setFromToken(basics.token);
        }
    }, [
        embedded,
        basics?.userName,
        basics?.amount,
        basics?.token?.address,
    ]);

    // Snapshot of the payment being submitted; needed because form state is
    // cleared on success but we still want to persist the original details.
    const pendingPaymentRef = useRef(null);
    const recordedTxRef = useRef(null);

    const pendingPaymentStorageKey = walletAddress
        ? `cronpay_pending_direct_${walletAddress.toLowerCase()}`
        : null;

    const readPendingPaymentSnapshot = () => {
        if (pendingPaymentRef.current) return pendingPaymentRef.current;
        if (!pendingPaymentStorageKey) return null;
        try {
            const raw = sessionStorage.getItem(pendingPaymentStorageKey);
            return raw ? JSON.parse(raw) : null;
        } catch (_error) {
            return null;
        }
    };

    const writePendingPaymentSnapshot = (snapshot) => {
        pendingPaymentRef.current = snapshot;
        if (!pendingPaymentStorageKey || !snapshot) return;
        try {
            sessionStorage.setItem(
                pendingPaymentStorageKey,
                JSON.stringify(snapshot),
            );
        } catch (_error) {
            // sessionStorage may be unavailable; in-memory ref still works.
        }
    };

    const clearPendingPaymentSnapshot = () => {
        pendingPaymentRef.current = null;
        if (!pendingPaymentStorageKey) return;
        try {
            sessionStorage.removeItem(pendingPaymentStorageKey);
        } catch (_error) {
            // ignore
        }
    };

    const onWriteError = (error) => {
        if (error) {
            if (!error?.details?.includes('MetaMask Tx Signature: User denied transaction signature')) {
                fireErrorToast(TRANSACTION_ERROR_MSG)
            }
            if (error?.details?.includes('MetaMask Tx Signature: User denied transaction signature')) {
                fireErrorToast(USER_REJECT_TRANSACTION_MSG)
            }
        }
        setLoading(false)
    }

    // **********************************************************Approve Payment********************************************************************* //

    const { writeContract: approve, writeSuccessResponse: approveResponse, writeErrorResponse: approveErrorResponse } = ContractWrite({ address: fromToken?.address, abi: ERC20ABI, functionName: 'approve', initialArgs: [CPGAddress, 0n] });



    useEffect(() => {
        if (approveResponse) {
            if (!ethers.isAddress(directPaymentData.userName)) {
                fireErrorToast('Enter a valid receiver wallet address')
                setLoading(false)
                return
            }
            const fromAmount = ethers.parseUnits(directPaymentData?.fromAmount.toString(), fromToken?.decimals)

            directPayment({
                abi: CPGABI,
                address: CPGAddress,
                functionName: 'initiateDirectPayment',
                args: [
                    directPaymentData.userName,
                    fromToken?.address,
                    fromAmount.toString(),
                    paymentRefBytes32(note),
                ],
            })
        }
        // eslint-disable-next-line
    }, [approveResponse])

    useEffect(() => {
        if (directPaymentData.fromAmount && fromToken.decimals) {

            // const fromAmount = convertInTokenBigAmount(directPaymentData.fromAmount, fromToken?.decimals)
            const fromAmount = ethers.parseUnits(directPaymentData?.fromAmount.toString(), fromToken?.decimals)

            directPaymentArgs([
                directPaymentData.userName,
                fromToken?.address ? fromToken?.address : ZERO_ADDRESS,
                fromAmount.toString(),
                paymentRefBytes32(note),
            ])
        }
        // eslint-disable-next-line
    }, [directPaymentData, fromToken])

    useEffect(() => {
        if (approveErrorResponse) {
            onWriteError(approveErrorResponse);
        }
        // eslint-disable-next-line
    }, [approveErrorResponse]);

    // **********************************************************Approve Payment********************************************************************* //

    // **********************************************************Direct Payment********************************************************************* //

    const { writeContract: directPayment, writeSuccessResponse: directPaymentResponse, writeErrorResponse: directPaymentErrorResponse, updateArgs: directPaymentArgs, transactionHash: directPaymentTxHash } = ContractWrite({ address: CPGAddress, abi: CPGABI, functionName: 'initiateDirectPayment', initialArgs: [directPaymentData.userName, fromToken?.address ?? ZERO_ADDRESS, directPaymentData.fromAmount ?? 1, paymentRefBytes32(note)], enabled: true });

    const persistDirectPayment = async (txHash) => {
        const snapshot = readPendingPaymentSnapshot();
        if (!snapshot || !txHash || !walletAddress) {
            console.warn(
                'Skipped backend save for direct payment:',
                !snapshot ? 'missing payment snapshot' : !txHash ? 'missing tx hash' : 'wallet disconnected',
            );
            if (!txHash) {
                fireErrorToast(
                    'Payment confirmed on-chain but transaction hash was missing. Refresh and check dashboard history.',
                );
            }
            return;
        }
        if (recordedTxRef.current === txHash) return;
        recordedTxRef.current = txHash;

        try {
            let onChainPaymentId = null;
            let blockNumber = null;
            try {
                const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
                blockNumber =
                    receipt?.blockNumber !== undefined && receipt?.blockNumber !== null
                        ? receipt.blockNumber.toString()
                        : null;

                for (const log of receipt?.logs || []) {
                    try {
                        const decoded = decodeEventLog({
                            abi: CPGABI,
                            data: log.data,
                            topics: log.topics,
                        });
                        onChainPaymentId = decodePaymentCreatedId(decoded);
                        if (onChainPaymentId) break;
                    } catch (_decodeError) {
                        // Non-CPG log (e.g. ERC20 Transfer); ignore.
                    }
                }
            } catch (decodeError) {
                console.warn(
                    'Failed to decode payment receipt for paymentId:',
                    decodeError?.message,
                );
            }

            const result = await persistPaymentRecord(
                walletAddress,
                {
                    kind: 'direct',
                    paymentId: onChainPaymentId,
                    txHash,
                    sender: walletAddress,
                    receiver: snapshot.receiver,
                    tokenAddress: snapshot.tokenAddress,
                    tokenSymbol: snapshot.tokenSymbol || null,
                    tokenDecimals: snapshot.tokenDecimals ?? null,
                    amount: snapshot.amount,
                    amountWei: snapshot.amountWei,
                    chainId: targetChainId,
                    blockNumber,
                    status: 'confirmed',
                    meta: snapshot.note ? { note: snapshot.note } : null,
                },
                { signMessageAsync },
            );

            if (!result.ok) {
                console.warn('Failed to persist direct payment to backend:', result.reason);
                fireErrorToast(
                    `Payment succeeded on-chain but was not saved to the server. ${result.reason}`,
                );
            }
        } catch (error) {
            console.warn(
                'Failed to persist direct payment to backend:',
                error?.response?.data?.message || error?.message,
            );
            fireErrorToast(
                'Payment succeeded on-chain but could not be saved to the server.',
            );
        } finally {
            clearPendingPaymentSnapshot();
        }
    };

    useEffect(() => {
        if (!directPaymentResponse || !directPaymentTxHash) return;

        persistDirectPayment(directPaymentTxHash);
        setLoading(false);
        setDirectPaymentData({ userName: '', fromAmount: '', to: '' });
        setFromToken({});
        setNote('');
        fireSuccessToast(TRANSACTION_SUCCESS_MSG);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directPaymentResponse, directPaymentTxHash]);

    useEffect(() => {
        if (directPaymentData.fromAmount && fromToken.decimals) {

            const fromAmount = ethers.parseUnits(directPaymentData?.fromAmount.toString(), fromToken?.decimals)
            directPaymentArgs([
                directPaymentData.userName,
                fromToken?.address ? fromToken?.address : ZERO_ADDRESS,
                fromAmount.toString(),
                paymentRefBytes32(note),
            ])
        }
        // eslint-disable-next-line
    }, [directPaymentData, fromToken])


    useEffect(() => {
        if (directPaymentErrorResponse) {
            onWriteError(directPaymentErrorResponse);
        }
        // eslint-disable-next-line
    }, [directPaymentErrorResponse]);

    // **********************************************************Direct Payment********************************************************************* //

    useEffect(() => {
        const checkAddress = async () => {
            if (ethers.isAddress(directPaymentData.userName)) {
                const result = await isContractAddress(directPaymentData.userName);
                setIsRecipientContract(result);
            } else {
                setIsRecipientContract(false);
            }
        };
        checkAddress();
    }, [directPaymentData.userName]);

    const handleSelectFromToken = (item) => {
        setFromToken(item);
    };

    const handleChange = (e) => {
        setDirectPaymentData({ ...directPaymentData, [e.target.name]: e.target.value })
    }


    const handleSubmit = async (e) => {
        try {
            setLoading(true)
            e.preventDefault()
            if (!ethers.isAddress(directPaymentData.userName)) {
                fireErrorToast('Enter a valid receiver wallet address')
                setLoading(false)
                return
            }
            if (directPaymentData.userName.toLowerCase() === walletAddress?.toLowerCase()) {
                fireErrorToast(SELF_PAYMENT_WARNING_MSG)
                setLoading(false)
                return
            }
            const isContract = await isContractAddress(directPaymentData.userName);
            if (isContract) {
                fireErrorToast(CONTRACT_ADDRESS_WARNING_MSG)
                setLoading(false)
                return
            }
            const allowance = await getAllowance(fromToken.address, walletAddress)

            const convertedAmount = convertInTokenBigAmount(directPaymentData.fromAmount, fromToken.decimals)

            const fromAmountUnits = ethers.parseUnits(
                directPaymentData.fromAmount.toString(),
                fromToken.decimals,
            )
            writePendingPaymentSnapshot({
                receiver: directPaymentData.userName,
                tokenAddress: fromToken.address,
                tokenSymbol: fromToken.symbol || fromToken.name || null,
                tokenDecimals: fromToken.decimals ?? null,
                amount: directPaymentData.fromAmount,
                amountWei: fromAmountUnits.toString(),
                note: note?.trim() || null,
            });
            recordedTxRef.current = null;

            if (Number(allowance) < Number(convertedAmount)) {
                try {
                    approve({
                        abi: ERC20ABI,
                        address: fromToken?.address,
                        functionName: 'approve',
                        args: [CPGAddress, fromAmountUnits],
                    });
                } catch (error) {
                    console.log(error);

                }

            } else {
                const fromAmount = ethers.parseUnits(directPaymentData?.fromAmount.toString(), fromToken?.decimals)
                try {
                    directPayment({
                        abi: CPGABI,
                        address: CPGAddress,
                        functionName: 'initiateDirectPayment',
                        args: [
                            directPaymentData.userName,
                            fromToken?.address,
                            fromAmount.toString(),
                            paymentRefBytes32(note),
                        ],
                    })
                } catch (error) {
                    console.log(error);

                }
            }

        } catch (error) {
            console.log(error);
            setLoading(false)

        }

    }


    useEffect(() => {
        let nameOfButton = "Enter Detail"
        let buttonColor = "bg-gray-300"
        let textColor = "text-gray-600"
        let disable = true

        if (directPaymentData.fromAmount && fromToken.decimals) {
            const balance = convertBigToSmallDigitWithSpecificDecimal(fromToken.balance, fromToken.decimals)
            if (Number(directPaymentData.fromAmount) > Number(balance)) {
                nameOfButton = "Insufficient Balance"
                buttonColor = "bg-red-700"
                textColor = "text-white"
                disable = true

            } else if (directPaymentData.userName && directPaymentData.userName.toLowerCase() === walletAddress?.toLowerCase()) {
                nameOfButton = "Cannot pay to your own address"
                buttonColor = "bg-red-700"
                textColor = "text-white"
                disable = true

            } else if (isRecipientContract) {
                nameOfButton = "Cannot pay to contract address"
                buttonColor = "bg-red-700"
                textColor = "text-white"
                disable = true

            } else if (!directPaymentData.userName) {
                nameOfButton = "Enter Detail"
                buttonColor = "bg-gray-300"
                textColor = "text-gray-600"
                disable = true
            }
            else {
                nameOfButton = "Proceed to the Payment"
                buttonColor = "bg-teal-700 hover:bg-teal-600"
                textColor = "text-white"
                disable = false
            }
        }
        setButtonDetail({ name: nameOfButton, bgColor: buttonColor, textColor, isDisabled: disable })
        // eslint-disable-next-line
    }, [directPaymentData, fromToken, isRecipientContract])



    const inputClass = 'cpg-input';
    const labelClass =
        'flex items-center gap-1 text-[13px] font-semibold text-zinc-900 dark:text-zinc-200';

    const tokenBalanceFormatted = fromToken.decimals !== undefined
        ? convertBigToSmallDigitWithSpecificDecimal(fromToken.balance, fromToken.decimals)
        : null;

    const isInvalidAddress = directPaymentData.userName && !ethers.isAddress(directPaymentData.userName);
    const isFormReady = directPaymentData.userName && fromToken.address && directPaymentData.fromAmount;

    const onMaxClick = () => {
        if (!fromToken.decimals || !fromToken.balance) return;
        const max = convertBigToSmallDigitWithSpecificDecimal(
            fromToken.balance,
            fromToken.decimals,
        );
        setDirectPaymentData((prev) => ({ ...prev, fromAmount: String(max ?? '') }));
    };

    return (
        <form className="space-y-5">
            {!embedded && (
                <>
                    {/* Receiver wallet address */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label htmlFor="userName" className={labelClass}>
                                Receiver Wallet Address <span className="text-rose-600 dark:text-rose-400">*</span>
                            </label>
                            <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
                                EVM-compatible address only
                            </span>
                        </div>
                        <input
                            type="text"
                            id="userName"
                            name="userName"
                            value={directPaymentData.userName}
                            onChange={handleChange}
                            placeholder="0x... (42 characters)"
                            autoComplete="off"
                            spellCheck={false}
                            className={`${inputClass} font-mono ${isInvalidAddress ? 'border-rose-500/50 focus:ring-rose-500/40 focus:border-rose-500/50' : ''}`}
                            required
                        />
                        {isInvalidAddress && (
                            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-300">
                                <i className="fa-solid fa-circle-exclamation text-[10px]" />
                                Address must be a valid 0x… EVM wallet.
                            </p>
                        )}
                    </div>

                    {/* Token + Amount */}
                    <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
                        <div>
                            <label className={labelClass}>
                                Token <span className="text-rose-600 dark:text-rose-400">*</span>
                            </label>
                            <div className="relative mt-2">
                                <Token handleSelectToken={handleSelectFromToken} token={fromToken} />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label htmlFor="fromAmount" className={labelClass}>
                                    Amount <span className="text-rose-600 dark:text-rose-400">*</span>
                                </label>
                                {fromToken.name && tokenBalanceFormatted !== null && (
                                    <button
                                        type="button"
                                        onClick={onMaxClick}
                                        className="text-[11px] font-bold text-emerald-700 transition-colors hover:text-emerald-800 tabular-nums dark:text-emerald-300 dark:hover:text-emerald-200"
                                        title="Use max balance"
                                    >
                                        Max: {tokenBalanceFormatted} {fromToken.symbol || ''}
                                    </button>
                                )}
                            </div>
                            <input
                                type="number"
                                id="fromAmount"
                                min={0}
                                onWheel={handleWheel}
                                onKeyDown={handleKeyDown}
                                name="fromAmount"
                                value={directPaymentData.fromAmount}
                                onChange={handleChange}
                                placeholder="0.00"
                                className={`${inputClass} tabular-nums`}
                                required
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Optional note */}
            <div>
                <label htmlFor="note" className={labelClass}>
                    Note <span className="font-normal text-zinc-500 dark:text-zinc-500">(optional)</span>
                </label>
                <p className="mb-2 mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-500">
                    A short description stored off-chain for your reference.
                </p>
                <textarea
                    id="note"
                    name="note"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Invoice #2024-089 — Design services April"
                    maxLength={280}
                    className={`${inputClass} resize-none`}
                />
            </div>

            {/* Warning banner */}
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] px-3.5 py-3 flex items-start gap-2.5">
                <i className="fa-solid fa-circle-info mt-0.5 text-sm text-amber-600 dark:text-amber-400" />
                <p className="text-[12px] leading-relaxed text-zinc-700 dark:text-zinc-300">
                    <span className="font-semibold text-amber-800 dark:text-amber-300">
                        Double-check the address.
                    </span>{' '}
                    Crypto transactions are irreversible. CronPay cannot recover funds
                    sent to the wrong address.
                </p>
            </div>

            {/* Submit */}
            {loading ? (
                <div className="flex w-full items-center justify-center rounded-xl bg-zinc-100 py-3 text-sm font-semibold text-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-300">
                    <Loader stroke="#34d399" size="22px" />
                </div>
            ) : isFormReady && !buttonDetail.isDisabled ? (
                <button
                    type="submit"
                    onClick={handleSubmit}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                >
                    <i className="fa-solid fa-paper-plane text-xs" />
                    Send Payment
                </button>
            ) : (
                <button
                    type="button"
                    disabled
                    className={`inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold ${
                        buttonDetail.bgColor === 'bg-red-700'
                            ? 'border border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-300'
                            : 'border border-zinc-200 bg-zinc-100 text-zinc-500 dark:border-zinc-700/60 dark:bg-zinc-800/70 dark:text-zinc-500'
                    }`}
                >
                    <i className="fa-solid fa-lock text-[11px]" />
                    {buttonDetail.name || 'Enter Detail'}
                </button>
            )}
        </form>
    )
}

export default DirectPayment
